/**
 * Data layer for the Deliveries table — query-param assembly + the GRN list
 * and tab-count fetches. Extracted from DeliveryTab.tsx (pure, no React state)
 * to keep that component under the 500-line limit. The component keeps
 * ownership of loading/error/selection state and just calls fetchDeliveriesData.
 *
 * @link DeliveryTab for the consumer.
 */
import { purchasingAPI } from '../../../../lib/api';
import type { GoodsReceiptNote } from '../../../../lib/api/types/grn-types';

/** Flat filter state driving the Deliveries list (all string values). */
export interface DeliveryFilters {
  grnNumber: string;
  poNumber: string;
  supplier: string;
  // CR-7342: free-text supplier name, distinct from `supplier` above (which is
  // the filter panel's exact company id). Kept as its own key so the quick
  // search and the panel's dropdown can both be set without either clobbering
  // the other.
  supplierName: string;
  partNumber: string;
  lineNumber: string;
  inspection_status: string;
  dateFrom: string;
  dateTo: string;
  site: string;
  warehouse: string;
  supplierGroup: string;
  commodityGroup: string;
  commodityType: string;
  appealStatus: string;
  appealFilter: string;
  poLineStatus: string;
  grnStatus: string;
  oddFrom: string;
  oddTo: string;
  strategic: string;
  supplierCode: string;
  // CR-6442 rework: the design's remaining filters, each backed by a lookup
  // the GRN viewset now accepts.
  customerCountry: string;
  division: string;
  supplierCountry: string;
  supplierType: string;
  buyerCode: string;
  strategicOnly: string;
}

function formatLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function getThisWeekMonday(): string {
  const today = new Date();
  const daysSinceMonday = (today.getDay() + 6) % 7;
  return formatLocalDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday));
}
export function getThisWeekSunday(): string {
  const today = new Date();
  const daysSinceMonday = (today.getDay() + 6) % 7;
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday);
  return formatLocalDate(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6));
}
/** Receipt-date floor for the "Last 4 Weeks" tab — 28 days back from today. */
export function getFourWeeksAgo(): string {
  const today = new Date();
  return formatLocalDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 28));
}
/** Receipt-date floor for the "Early & Late" tab — 12 months back from today. */
export function getTwelveMonthsAgo(): string {
  const today = new Date();
  return formatLocalDate(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()));
}

export interface FetchDeliveriesArgs {
  filters: DeliveryFilters;
  activeTab: string;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  currentPage: number;
  pageSize: number;
  mySuppliersOnly: boolean;
  buyerCodes: string[];
  viewParams: Record<string, unknown>;
}

/** Build the GRN list query params from the current filter/tab/scope state. */
export function buildGrnListParams(a: FetchDeliveriesArgs): Record<string, string | number | boolean> {
  const { filters, activeTab, sortField, sortDirection, currentPage, pageSize, mySuppliersOnly, buyerCodes, viewParams } = a;
  const orderingPrefix = sortDirection === 'desc' ? '-' : '';
  const queryParams: Record<string, string | number | boolean> = {
    ordering: `${orderingPrefix}${sortField}`,
    page: currentPage,
    page_size: pageSize,
  };
  // Buyer-code scope override (backend defaults to "my suppliers").
  if (buyerCodes.length > 0 && !mySuppliersOnly) queryParams['all_suppliers'] = true;

  // CR-4665 rework: pass the active view-context scope to the API.
  if (viewParams['group']) queryParams['group'] = viewParams['group'] as number;
  if (viewParams['company']) queryParams['company'] = viewParams['company'] as number;
  if (viewParams['site']) queryParams['site'] = viewParams['site'] as number;

  if (filters.grnNumber) queryParams['grn_number__icontains'] = filters.grnNumber;
  if (filters.poNumber) queryParams['po_line__po__po_number__icontains'] = filters.poNumber;
  if (filters.supplier) queryParams['po_line__po__supplier'] = filters.supplier;
  // CR-7342: the quick search matches on supplier NAME, and is a separate param
  // from the panel's exact-id `supplier` above. The lookup was already
  // whitelisted on the GRN viewset's filterset_fields, so this needs no backend
  // change. django-filter ANDs it with every other param, so it narrows an
  // existing selection rather than replacing it. Applied to the export params
  // as well as the list, so a filtered export matches what the grid shows.
  if (filters.supplierName) {
    queryParams['po_line__po__supplier__name__icontains'] = filters.supplierName;
  }
  if (filters.partNumber) queryParams['po_line__part_number__icontains'] = filters.partNumber;

  if (filters.inspection_status) queryParams['inspection_status'] = filters.inspection_status;
  // On-time status from tab.
  if (activeTab === 'on_time') {
    queryParams['on_time_status'] = 'on_time';
  } else if (activeTab === 'early_late') {
    queryParams['on_time_status__in'] = 'early,late';
  }

  // Date filters (panel inputs win; the date-scoped tabs supply their range).
  // Early & Late defaults to the last 12 months so the tab isn't dominated by
  // years of historical exceptions (the date panel can still widen it).
  if (filters.dateFrom) {
    queryParams['receipt_date__gte'] = filters.dateFrom;
  } else if (activeTab === 'delivered_this_week') {
    queryParams['receipt_date__gte'] = getThisWeekMonday();
  } else if (activeTab === 'last_4_weeks') {
    queryParams['receipt_date__gte'] = getFourWeeksAgo();
  } else if (activeTab === 'early_late') {
    queryParams['receipt_date__gte'] = getTwelveMonthsAgo();
  }
  if (filters.dateTo) {
    queryParams['receipt_date__lte'] = filters.dateTo;
  } else if (activeTab === 'delivered_this_week') {
    queryParams['receipt_date__lte'] = getThisWeekSunday();
  }

  if (filters.lineNumber) queryParams['po_line__line_number'] = filters.lineNumber;
  if (filters.warehouse) queryParams['warehouse'] = filters.warehouse;
  if (filters.site) queryParams['po_line__po__customer'] = filters.site;
  if (filters.supplierGroup) queryParams['po_line__po__supplier__group'] = filters.supplierGroup;
  if (filters.commodityGroup) queryParams['po_line__commodity_group'] = filters.commodityGroup;
  if (filters.commodityType) queryParams['po_line__commodity_type'] = filters.commodityType;
  if (filters.poLineStatus === 'open') {
    queryParams['po_line__status__in'] = 'pending,partially_received';
  } else if (filters.poLineStatus === 'closed') {
    queryParams['po_line__status__in'] = 'received,cancelled';
  }
  if (filters.grnStatus === 'early_late') {
    queryParams['on_time_status__in'] = 'early,late';
  } else if (filters.grnStatus) {
    queryParams['on_time_status'] = filters.grnStatus;
  }
  // Customer review 11 Sep (stream I, from E): ODD is THE verdict anchor (the
  // date the ODD column shows), filtered server-side — not the re-planned
  // po_line.customer_required_date the old keys compared against.
  if (filters.oddFrom) queryParams['odd_from'] = filters.oddFrom;
  if (filters.oddTo) queryParams['odd_to'] = filters.oddTo;
  if (filters.appealStatus) queryParams['workflow_status'] = filters.appealStatus;
  if (filters.appealFilter === 'no_appeal') {
    queryParams['appeal_count'] = 0;
  } else if (filters.appealFilter === 'appealed') {
    queryParams['appeal_count__gt'] = 0;
  }
  // CR-7028: Use the supplier company's supplier_code (not the PO line's, which
  // is often empty on import paths). This aligns list and export to the same field.
  if (filters.supplierCode) queryParams['po_line__po__supplier__supplier_code__icontains'] = filters.supplierCode;

  // CR-6442 rework: the design's remaining filters. Country / order type /
  // buyer code go through whitelisted filterset lookups; division, supplier
  // type and strategic-only are resolved server-side in the viewset because
  // each needs more than a field comparison (group expansion, the customer's
  // own group, and the (supplier, customer) relationship respectively).
  if (filters.customerCountry) queryParams['po_line__po__customer__country'] = filters.customerCountry;
  if (filters.supplierCountry) queryParams['po_line__po__supplier__country'] = filters.supplierCountry;
  if (filters.buyerCode) queryParams['po_line__buyer_id__icontains'] = filters.buyerCode;
  if (filters.division) queryParams['division'] = filters.division;
  if (filters.supplierType) queryParams['supplier_type'] = filters.supplierType;
  if (filters.strategicOnly === 'true') queryParams['strategic_only'] = 'true';

  return queryParams;
}

/**
 * Build the CSV-export query params.
 *
 * The export action runs ``filter_queryset(get_queryset())`` on the same
 * GoodsReceiptNoteViewSet as the list, so it must speak the same filterset:
 * every key here has to be one the backend declares, or django-filter drops it
 * without erroring and the download comes back unfiltered on that axis.
 *
 * What differs from the list builder is scope, not vocabulary — the export
 * sends no pagination, and always sends an explicit ``all_suppliers`` so the
 * download honours the My/All-suppliers toggle.
 *
 * CR-7028 aligned the supplier code (``po_line__po__supplier__supplier_code__icontains``)
 * and CR-7268 the appeal filter (``appeal_count``); the earlier note here
 * claiming ``supplier__entity_code`` / ``has_appeal`` described neither the
 * backend nor, after those two fixes, this function.
 */
export function buildDeliveriesExportParams(a: FetchDeliveriesArgs): Record<string, string | number | boolean> {
  const { filters, activeTab, mySuppliersOnly, buyerCodes, viewParams } = a;
  const queryParams: Record<string, string | number | boolean> = {};

  if (buyerCodes.length > 0) queryParams['all_suppliers'] = !mySuppliersOnly;

  if (viewParams['group']) queryParams['group'] = viewParams['group'] as number;
  if (viewParams['company']) queryParams['company'] = viewParams['company'] as number;
  if (viewParams['site']) queryParams['site'] = viewParams['site'] as number;

  // Text filters
  if (filters.grnNumber) queryParams['grn_number__icontains'] = filters.grnNumber;
  if (filters.poNumber) queryParams['po_line__po__po_number__icontains'] = filters.poNumber;
  if (filters.supplier) queryParams['po_line__po__supplier'] = filters.supplier;
  // CR-7342: the quick search matches on supplier NAME, and is a separate param
  // from the panel's exact-id `supplier` above. The lookup was already
  // whitelisted on the GRN viewset's filterset_fields, so this needs no backend
  // change. django-filter ANDs it with every other param, so it narrows an
  // existing selection rather than replacing it. Applied to the export params
  // as well as the list, so a filtered export matches what the grid shows.
  if (filters.supplierName) {
    queryParams['po_line__po__supplier__name__icontains'] = filters.supplierName;
  }
  if (filters.partNumber) queryParams['po_line__part_number__icontains'] = filters.partNumber;
  if (filters.lineNumber) queryParams['po_line__line_number'] = filters.lineNumber;
  // CR-7028: Use the supplier company's supplier_code (aligned with list query).
  if (filters.supplierCode) queryParams['po_line__po__supplier__supplier_code__icontains'] = filters.supplierCode;

  // Dropdown filters
  if (filters.site) queryParams['po_line__po__customer'] = filters.site;
  if (filters.warehouse) queryParams['warehouse'] = filters.warehouse;
  if (filters.supplierGroup) queryParams['po_line__po__supplier__group'] = filters.supplierGroup;
  if (filters.commodityGroup) queryParams['po_line__commodity_group'] = filters.commodityGroup;
  if (filters.commodityType) queryParams['po_line__commodity_type'] = filters.commodityType;

  if (filters.poLineStatus === 'open') queryParams['po_line__status__in'] = 'pending,partially_received';
  else if (filters.poLineStatus === 'closed') queryParams['po_line__status__in'] = 'received,cancelled';

  // On-Time Status (explicit panel value wins over the tab)
  if (filters.grnStatus === 'early_late') queryParams['on_time_status__in'] = 'early,late';
  else if (filters.grnStatus) queryParams['on_time_status'] = filters.grnStatus;
  else if (activeTab === 'on_time') queryParams['on_time_status'] = 'on_time';
  else if (activeTab === 'early_late') queryParams['on_time_status__in'] = 'early,late';

  // CR-7268: the same appeal params as the list builder. The export previously
  // sent `has_appeal`, which no filter declares, so django-filter dropped it and
  // a filtered export downloaded every row.
  if (filters.appealFilter === 'no_appeal') queryParams['appeal_count'] = 0;
  else if (filters.appealFilter === 'appealed') queryParams['appeal_count__gt'] = 0;

  // CR-7268: appeal STATUS (workflow_status) had no branch here at all, so a
  // drill-down into "appeals awaiting response" exported every row the same way.
  // The lookup is declared on the GRN filterset and already used by the list.
  if (filters.appealStatus) queryParams['workflow_status'] = filters.appealStatus;

  // Receipt date (panel wins, else the date-scoped tabs supply their range)
  if (filters.dateFrom) queryParams['receipt_date__gte'] = filters.dateFrom;
  else if (activeTab === 'delivered_this_week') queryParams['receipt_date__gte'] = getThisWeekMonday();
  else if (activeTab === 'last_4_weeks') queryParams['receipt_date__gte'] = getFourWeeksAgo();
  else if (activeTab === 'early_late') queryParams['receipt_date__gte'] = getTwelveMonthsAgo();
  if (filters.dateTo) queryParams['receipt_date__lte'] = filters.dateTo;
  else if (activeTab === 'delivered_this_week') queryParams['receipt_date__lte'] = getThisWeekSunday();

  // ODD — THE verdict anchor (attributed slot -> line ODD -> delivery date),
  // the same keys as the list builder.
  if (filters.oddFrom) queryParams['odd_from'] = filters.oddFrom;
  if (filters.oddTo) queryParams['odd_to'] = filters.oddTo;

  // CR-6442 rework: the same keys as the list builder. The export action runs
  // `filter_queryset(get_queryset())` on this very viewset, so both the
  // whitelisted lookups and the server-resolved ones (division / supplier type
  // / strategic-only) apply to the download exactly as they do to the grid.
  // Omitting them here meant a filtered view exported unfiltered rows.
  if (filters.customerCountry) queryParams['po_line__po__customer__country'] = filters.customerCountry;
  if (filters.supplierCountry) queryParams['po_line__po__supplier__country'] = filters.supplierCountry;
  if (filters.buyerCode) queryParams['po_line__buyer_id__icontains'] = filters.buyerCode;
  if (filters.division) queryParams['division'] = filters.division;
  if (filters.supplierType) queryParams['supplier_type'] = filters.supplierType;
  if (filters.strategicOnly === 'true') queryParams['strategic_only'] = 'true';

  return queryParams;
}

export interface DeliveriesTabCounts {
  all: number;
  on_time: number;
  early_late: number;
  last_4_weeks: number;
  delivered_this_week: number;
}

export interface DeliveriesResult {
  results: GoodsReceiptNote[];
  count: number;
  tabCounts: DeliveriesTabCounts;
}

/** Fetch the current page of GRNs plus the (tab-independent) per-tab counts. */
export async function fetchDeliveriesData(a: FetchDeliveriesArgs): Promise<DeliveriesResult> {
  const queryParams = buildGrnListParams(a);
  const response = await purchasingAPI.getGRNs(queryParams);

  // Tab counts run without the tab filter applied. CR-4564: page 1 avoids
  // "Invalid page" errors.
  const countParams: Record<string, string | number | boolean> = { ...queryParams, page: 1, page_size: 1 };
  delete countParams['on_time_status'];
  delete countParams['on_time_status__in'];
  // Drop any receipt_date narrowing the tab applied so 'delivered_this_week'
  // counts the full week, not the active tab's sub-range.
  const tabOriginatedDateFilter = !a.filters.dateFrom && !a.filters.dateTo;
  if (tabOriginatedDateFilter) {
    delete countParams['receipt_date__gte'];
    delete countParams['receipt_date__lte'];
  }
  const thisWeekCountParams = {
    ...countParams,
    receipt_date__gte: a.filters.dateFrom || getThisWeekMonday(),
    receipt_date__lte: a.filters.dateTo || getThisWeekSunday(),
  };
  // Last 4 weeks: receipts from 28 days ago up to now (panel dates still win).
  const lastFourWeeksCountParams = {
    ...countParams,
    receipt_date__gte: a.filters.dateFrom || getFourWeeksAgo(),
    ...(a.filters.dateTo ? { receipt_date__lte: a.filters.dateTo } : {}),
  };
  // Early & Late count mirrors the tab's default 12-month floor.
  const earlyLateCountParams = {
    ...countParams,
    on_time_status__in: 'early,late',
    receipt_date__gte: a.filters.dateFrom || getTwelveMonthsAgo(),
    ...(a.filters.dateTo ? { receipt_date__lte: a.filters.dateTo } : {}),
  };
  const [allResp, onTimeResp, earlyLateResp, lastFourWeeksResp, deliveredThisWeekResp] = await Promise.all([
    purchasingAPI.getGRNs({ ...countParams }),
    purchasingAPI.getGRNs({ ...countParams, on_time_status: 'on_time' }),
    purchasingAPI.getGRNs(earlyLateCountParams),
    purchasingAPI.getGRNs(lastFourWeeksCountParams),
    purchasingAPI.getGRNs(thisWeekCountParams),
  ]);

  return {
    results: response.data.results,
    count: response.data.count,
    tabCounts: {
      all: allResp.data.count,
      on_time: onTimeResp.data.count,
      early_late: earlyLateResp.data.count,
      last_4_weeks: lastFourWeeksResp.data.count,
      delivered_this_week: deliveredThisWeekResp.data.count,
    },
  };
}
